import { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { Button } from "../../common/Button";
import { Table, Row, Col } from "react-bootstrap";
import Select from "react-select";
import moment from "moment";
import { AddCircleOutline, DescriptionOutlined, History } from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import StatusHistoryModal from "./StatusHistoryModal";
import ViewNotesModal from "./ViewNotesModal";
import { updateTask } from "../../api/Task";
import { useSelector } from "react-redux";
import { failed, success } from "../../common/Toastify";
import { capitalizeWords } from "../../common/functions";

const STATUS_OPTIONS = [
    { value: "new", label: "New" },
    { value: "in-progress", label: "In Progress" },
    { value: "completed", label: "Completed" },
];

export default function TaskDetailsModal({ task, show, onHide, onStatusSaved, onNoteAdded, organizations }) {
    const [status, setStatus] = useState(task.status);
    const [saving, setSaving] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [showNotes,  setShowNotes]    = useState(false);

    const currentUserName = useSelector(s => s?.auth?.user?.name?.[0]?.text);
    const orgId = useSelector(s => s?.auth?.user?.organizations?.[0]?.id);
    const currentUserId = useSelector(s => s?.auth?.user?.["custom:unique_id"]);

    useEffect(() => {
      setStatus(task.status);
    }, [task.status]);

    const handleSave = async () => {
        if (status === task.status) { onHide(); return; }
        try {
            setSaving(true);

            const now = Date.now();
            const history = [...(task.statusHistory ?? [])];

            if (history.length) {
                const last = { ...history.at(-1) };
                last.period = { ...last.period, end: now };
                history[history.length - 1] = last;
            }

            const newEntry = {
                actor: {
                    reference: `Practitioner/${currentUserId}`,
                    display: currentUserName,
                },
                period: { start: now, end: null },
                status,
            };
            history.push(newEntry);

            await updateTask(
                { id: task.id, status, statusHistory: history, orgId: task.orgId },
            );

            success("Task status updated");
            onStatusSaved(task.id, status, history);
            onHide();
        } catch (err) {
            failed(err?.response?.data?.message || err.message);
        } finally { setSaving(false); }
    };


    const isLocked = task.status === "completed";

    return (
        <>
            <Modal backdrop="static" centered show={show} onHide={onHide} size="lg" className="custom-dialog">
                <Modal.Header closeButton>
                    <Modal.Title>Task Details</Modal.Title>
                </Modal.Header>

                <Modal.Body>
                    {/* ───────────────────── 1. Task-Creation Details ───────────────────── */}
                    <b>Task Creation Details</b>
                    <Row className="mb-3 mt-2">
                        <Col>
                            <span className="label-grey">Created By:</span> {capitalizeWords(task.requesterDisplay)} <br />
                            <span className="label-grey">Created For:</span> {organizations.find(org => org.value === task.orgId)?.label} <br />
                            <span className="label-grey">Created Date:</span> {moment(task.created).format("MMM-DD-YYYY")} <br />
                            <span className="label-grey">Created Time:</span> {moment(task.created).format("HH:mm")}
                        </Col>
                    </Row>

                    <hr />

                    {/* ───────────────────── 2. Task Details ───────────────────────────── */}
                    <b>Task Details</b>
                    <Row className="mb-3 mt-2">
                        <Col md={6}>
                            <div><span className="label-grey">Type:</span> {task.type}</div>
                            <div><span className="label-grey">Description:</span> {task.description}</div>
                            <div><span className="label-grey">Priority:</span> {task.priority}</div>
                            <div>
                                <span className="label-grey">Notes:</span>{" "}
                                {task.notes?.length ? (
                                    <Tooltip title="View Notes">
                                        <span style={{ cursor: "pointer" }} onClick={() => setShowNotes(true)}>
                                            <DescriptionOutlined style={{ color: "#005ba8" }} />
                                        </span>
                                    </Tooltip>
                                ) : "—"}
                            </div>
                        </Col>

                        <Col md={6}>
                            <div><span className="label-grey">Practitioner:</span> {task.owner?.display || "-"}</div>
                            <div>
                                <span className="label-grey">Patient:</span>{" "}
                                <a
                                    href={`/app/patients-details?patientId=${task.patient?.reference?.split("/")[1]}`}//&patientName=${task.patient?.display}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ color: "#0064a6" }}
                                >
                                    {task.patient?.display}
                                </a>
                            </div>
                        </Col>
                    </Row>

                    {/* ───────────────────── 3. Status row ─────────────────────────────── */}
                    <Row className="mb-3">
                        <Col md={6} className="d-flex align-items-center">
                            <span className="label-grey" style={{ marginRight: 8 }}>Status:</span>
                            <Select
                                value={STATUS_OPTIONS.find(o => o.value === status)}
                                onChange={o => setStatus(o.value)}
                                options={STATUS_OPTIONS}
                                isDisabled={isLocked}
                                styles={{ container: base => ({ ...base, flex: 1 }) }}
                            />
                            {task.statusHistory?.length > 0 && (
                                <Tooltip title="Status History">
                                    <span style={{ marginLeft: 8, cursor: "pointer" }} onClick={() => setShowHistory(true)}>
                                        <History style={{ color: "#005ba8" }} />
                                    </span>
                                </Tooltip>
                            )}
                        </Col>
                    </Row>
                </Modal.Body>

                <Modal.Footer>
                    {!isLocked && (
                        <Button variant="secondary" onClick={onHide}>
                            Cancel
                        </Button>
                    )}
                    {!isLocked && (
                        <Button onClick={handleSave} isLoading={saving} disabled={isLocked}>
                            Save
                        </Button>
                    )}
                </Modal.Footer>
            </Modal>

            {/* nested modals */}
            {showHistory && (
                <StatusHistoryModal
                    history={task.statusHistory}
                    show={showHistory}
                    onHide={() => setShowHistory(false)}
                />
            )}

            {showNotes && (                                    
                    <ViewNotesModal
                    recordData={task}
                    onShow={showNotes}
                    onHide={() => setShowNotes(false)}
                    onNoteAdded={onNoteAdded}
                    />
                )}

        </>
    );
}
